import { useEffect, useMemo, useState } from "react";
import type { TipDetails } from "model/Tip";
import { hasApprovedTimeReports } from "../logic/tips.v2.logic";
import { TipsBulkActionsBar } from "./tips.bulkActionsBar.component";
import { TipPayoutModals } from "./tips.payoutModals.component";
import { TipRowItem } from "./tips.rowItem.component";
import {
  isActionableReadyToPayoutTip,
  isManualPaidOutTip,
  isReadyToSplitTip,
  isUnfilledTip,
} from "./tips.rows.logic";
import type { TipsRowsProps } from "./tips.rows.types";
import { TipRows } from "../tips.styled";

export const TipsRows = (props: TipsRowsProps): JSX.Element => {
  const [selectedTipIds, setSelectedTipIds] = useState<readonly number[]>([]);
  const [selectedPayoutTips, setSelectedPayoutTips] = useState<
    readonly TipDetails[]
  >([]);
  const [selectedBankTransferTips, setSelectedBankTransferTips] = useState<
    readonly TipDetails[]
  >([]);
  const [selectedSwishTips, setSelectedSwishTips] = useState<
    readonly TipDetails[]
  >([]);
  const [isPayoutSaving, setIsPayoutSaving] = useState(false);

  const selectedTips = useMemo(
    (): readonly TipDetails[] =>
      props.tips.filter((tip) => selectedTipIds.includes(tip.companyTipId)),
    [props.tips, selectedTipIds],
  );
  const selectedTotalAmount = selectedTips.reduce(
    (sum, tip) => sum + tip.amount,
    0,
  );
  const selectedCount = selectedTips.length;
  const selectedIds = selectedTips.map((tip) => tip.companyTipId);
  const canBulkRemove =
    selectedCount > 0 &&
    (selectedTips.every(isReadyToSplitTip) ||
      selectedTips.every(isUnfilledTip));
  const canBulkDistribute =
    selectedCount > 0 &&
    selectedTips.every(
      (tip) => isReadyToSplitTip(tip) && hasApprovedTimeReports(tip),
    );
  const shouldShowDisabledBulkDistribute =
    selectedCount > 0 &&
    selectedTips.every(isReadyToSplitTip) &&
    selectedTips.some((tip) => !hasApprovedTimeReports(tip));
  const canBulkPayout =
    selectedCount > 0 && selectedTips.every(isActionableReadyToPayoutTip);
  const canBulkRevert =
    selectedCount > 0 && selectedTips.every(isManualPaidOutTip);
  const hasBulkAction =
    canBulkRemove || canBulkDistribute || canBulkPayout || canBulkRevert;

  useEffect(() => {
    const visibleTipIds = new Set(props.tips.map((tip) => tip.companyTipId));
    setSelectedTipIds((currentIds) =>
      currentIds.filter((companyTipId) => visibleTipIds.has(companyTipId)),
    );
  }, [props.tips]);

  const clearSelection = (): void => {
    setSelectedTipIds([]);
  };

  const toggleSelectedTip = (companyTipId: number): void => {
    setSelectedTipIds((currentIds) =>
      currentIds.includes(companyTipId)
        ? currentIds.filter((id) => id !== companyTipId)
        : [...currentIds, companyTipId],
    );
  };

  const closeBankTransferModal = (): void => {
    if (isPayoutSaving) return;
    setSelectedBankTransferTips([]);
  };

  const closeSwishPaymentModal = (): void => {
    if (isPayoutSaving) return;
    setSelectedSwishTips([]);
  };

  const closePayoutModal = (): void => {
    setSelectedPayoutTips([]);
  };

  const selectBankTransferPayout = (): void => {
    setSelectedBankTransferTips(selectedPayoutTips);
    setSelectedPayoutTips([]);
  };

  const selectSwishPayout = (): void => {
    setSelectedSwishTips(selectedPayoutTips);
    setSelectedPayoutTips([]);
  };

  const handlePayout: TipsRowsProps["onPayout"] = async (
    companyTipIds,
    payoutType,
  ) => {
    setIsPayoutSaving(true);
    try {
      const result = await props.onPayout(companyTipIds, payoutType);
      if (result.ok) {
        setSelectedBankTransferTips([]);
        setSelectedSwishTips([]);
        clearSelection();
      }
      return result;
    } finally {
      setIsPayoutSaving(false);
    }
  };

  return (
    <>
      <TipRows>
        {props.tips.map((tip) => (
          <TipRowItem
            key={tip.companyTipId}
            isSelected={selectedTipIds.includes(tip.companyTipId)}
            tip={tip}
            onDelete={props.onDelete}
            onDistribute={props.onDistribute}
            onOpenPayout={(selectedTip) => setSelectedPayoutTips([selectedTip])}
            onRevertManualPayout={props.onRevertManualPayout}
            onToggleSelected={toggleSelectedTip}
            onUpdateAmount={props.onUpdateAmount}
          />
        ))}
      </TipRows>
      {selectedCount > 0 && (
        <TipsBulkActionsBar
          canBulkDistribute={canBulkDistribute}
          canBulkPayout={canBulkPayout}
          canBulkRemove={canBulkRemove}
          canBulkRevert={canBulkRevert}
          hasBulkAction={hasBulkAction}
          shouldShowDisabledBulkDistribute={shouldShowDisabledBulkDistribute}
          selectedIds={selectedIds}
          selectedTips={selectedTips}
          selectedTotalAmount={selectedTotalAmount}
          onClearSelection={clearSelection}
          onDeleteMany={props.onDeleteMany}
          onDistributeMany={props.onDistributeMany}
          onOpenPayout={setSelectedPayoutTips}
          onRevertManualPayout={props.onRevertManualPayout}
        />
      )}
      <TipPayoutModals
        isPayoutSaving={isPayoutSaving}
        selectedBankTransferTips={selectedBankTransferTips}
        selectedPayoutTips={selectedPayoutTips}
        selectedSwishTips={selectedSwishTips}
        onCloseBankTransferModal={closeBankTransferModal}
        onClosePayoutModal={closePayoutModal}
        onCloseSwishPaymentModal={closeSwishPaymentModal}
        onPayout={handlePayout}
        onSelectBankTransfer={selectBankTransferPayout}
        onSelectSwish={selectSwishPayout}
      />
    </>
  );
};
